import { isTypingTarget } from "../../ui/dom";

/**
 * Tracks held keys by `KeyboardEvent.code` (layout-independent physical keys, so WASD
 * works on AZERTY too). Browser default actions (Tab focus change, Space/arrow scrolling)
 * are suppressed for keys the game uses. Held keys are cleared when the window loses
 * focus so nothing stays stuck "down".
 */
export class KeyboardTracker {
	readonly held = new Set<string>();
	private readonly target: Window;
	private readonly captured: ReadonlySet<string>;

	constructor(captured: ReadonlySet<string>, target: Window = window) {
		this.captured = captured;
		this.target = target;
		target.addEventListener("keydown", this.onKeyDown);
		target.addEventListener("keyup", this.onKeyUp);
		target.addEventListener("blur", this.clear);
	}

	destroy(): void {
		this.target.removeEventListener("keydown", this.onKeyDown);
		this.target.removeEventListener("keyup", this.onKeyUp);
		this.target.removeEventListener("blur", this.clear);
		this.clear();
	}

	readonly clear = (): void => {
		this.held.clear();
	};

	private readonly onKeyDown = (ev: KeyboardEvent): void => {
		if (!this.captured.has(ev.code) || isTypingTarget(ev)) return;
		ev.preventDefault();
		this.held.add(ev.code);
	};

	private readonly onKeyUp = (ev: KeyboardEvent): void => {
		if (!this.captured.has(ev.code) || isTypingTarget(ev)) return;
		ev.preventDefault();
		this.held.delete(ev.code);
	};
}
